import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { socketService } from "@/services/socketService";
import { useAuth } from "@/auth/AuthContext";

/**
 * Bootstraps the Socket.IO connection for authenticated users.
 *
 * - Reads the active Supabase session access_token
 * - Initialises socketService with that token (auto-joins user:{userId} room on server)
 * - Re-initialises when the session changes (e.g. token refresh)
 * - Disconnects and cleans up on logout / unmount
 *
 * Mount this once inside the authenticated Layout so the socket is available
 * to all child components via socketService singleton.
 */
export function useSocketConnection() {
  const { session } = useAuth();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!session?.access_token) {
      // Signed out - disconnect if we were connected
      if (initializedRef.current) {
        socketService.disconnect();
        initializedRef.current = false;
      }
      return;
    }

    // If already connected with the same token, skip re-init
    if (initializedRef.current && socketService.isConnected()) return;

    socketService.initialize({
      authToken:    session.access_token,
      onConnect:    () => {},
      onDisconnect: () => {},
      onError:      () => {},
    });

    initializedRef.current = true;

    return () => {
      socketService.disconnect();
      initializedRef.current = false;
    };
  }, [session?.access_token]);
}
